import "./datatable.scss";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  monthlyresultColumns,
  fetchMonthlyresultRows,
} from "../../datatablesource";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSlug } from "../../SlugContext";
import { serverUrl } from "../../temp";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../loader/Loader";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";

const MonthlyResultDataTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classLoading, setClassLoading] = useState(false);
  const { slugs } = useSlug();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const roleString = localStorage.getItem("role_name") || "";
        const rolesArray = roleString.split(",").map((role) => role.trim());
        setRoles(rolesArray);

        // Fetch monthly results
        const response = await fetchMonthlyresultRows();

        if (!response || !response.data) {
          toast.warning("No monthly results data available");
          setData([]);
          return;
        }

        const resultData = Array.isArray(response.data) ? response.data : [];
        setData(resultData);

        if (rolesArray.includes("Admin")) {
          await fetchClassDropdown();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load monthly results");
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchClassDropdown = async () => {
    try {
      setClassLoading(true);
      const center_id = localStorage.getItem("center_id");
      if (!center_id) {
        toast.error("Center ID not found in local storage");
        return;
      }

      const response = await fetch(
        `${serverUrl}/result_dashboard/class_dropdown?centerId=${center_id}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch class dropdown");
      }

      const responseData = await response.json();

      if (!responseData || !responseData.data) {
        toast.warning("No class data available");
        setClasses([]);
        return;
      }

      const classData = Array.isArray(responseData.data)
        ? responseData.data
        : [];
      setClasses(classData);

      if (classData.length > 0) {
        const storedClassId = localStorage.getItem("Aclass_id");
        const initialClass =
          storedClassId && classData.find((c) => c.id === storedClassId)
            ? storedClassId
            : classData[0].id;
        setSelectedClass(initialClass);
        localStorage.setItem("Aclass_id", initialClass);
      }
    } catch (error) {
      console.error("Error fetching class dropdown:", error);
      toast.error("Failed to fetch class options");
      setClasses([]);
    } finally {
      setClassLoading(false);
    }
  };

  const handleClassChange = (event) => {
    const classId = event.target.value;
    setSelectedClass(classId);
    localStorage.setItem("Aclass_id", classId);
  };

  const handleDownloadResult = async (rowData) => {
    try {
      setLoading(true);
      toast.info("Preparing download...");

      const center_id = localStorage.getItem("center_id");
      const class_id = rowData.class_id || selectedClass;
      const month = rowData.month || "January";

      const params = new URLSearchParams({
        class_id,
        center_id,
        month,
      });

      const response = await fetch(
        `${serverUrl}/download-result-report?${params}`,

        // `${serverUrl}/download-subject-percentage?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const contentType = response.headers.get("content-type");

      // First check if the response is successful
      if (!response.ok) {
        // Handle all error responses (including 500)
        let errorData;

        try {
          // Try to parse as JSON first
          errorData = await response.json();
        } catch (jsonError) {
          // If not JSON, get the text response (for HTML errors)
          const errorText = await response.text();
          throw new Error(
            `Server error: ${response.status} - ${errorText.substring(
              0,
              200
            )}...`
          );
        }

        // Create user-friendly error messages
        let errorMessage = "";
        let errorTitle = "";

        if (errorData.validation_type === "exam_validation") {
          errorTitle = "📝 Exams Not Scheduled";
          errorMessage = `The following subjects exams are not being held:\n${errorData.missing_subjects.join(
            ", "
          )}`;
        } else if (errorData.validation_type === "award_list_validation") {
          errorTitle = "📊 Award Lists Missing";
          errorMessage = `Award lists are not filled for the following subjects:\n${errorData.missing_subjects.join(
            ", "
          )}`;
        } else if (errorData.data) {
          errorTitle = "👥 Missing Student Results";
          errorMessage = errorData.data
            .map(
              (student) =>
                `${student.roll_no} (${
                  student.name
                }): ${student.missing_subjects.join(", ")}`
            )
            .join("\n");
        } else {
          errorTitle = "❌ Error";
          errorMessage =
            errorData.message || `Server error: ${response.status}`;
        }

        // Show detailed error toast
        toast.error(
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              {errorTitle}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
          </div>,
          {
            autoClose: 2000,
            closeOnClick: false,
            draggable: false,
          }
        );
        return;
      }

      // If we get here, response is successful (200-299)
      if (
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        // Successful download handling
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers.get("content-disposition");
        let filename = `monthly_results_${class_id}_${month}.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("✅ Download started successfully!");
      } else {
        // Handle unexpected successful response format
        console.warn(
          "Unexpected content-type for successful response:",
          contentType
        );
        const responseText = await response.text();
        throw new Error(`Unexpected response format: ${contentType}`);
      }
    } catch (error) {
      console.error("Error downloading results:", error);

      // More specific error messages
      if (error.message.includes("Server error:")) {
        toast.error(`🚨 Server Error: ${error.message}`);
      } else if (error.message.includes("Unexpected response format")) {
        toast.error(
          "❌ Server returned unexpected format. Please check server logs."
        );
      } else {
        toast.error(`❌ ${error.message || "Failed to download results"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResultPercentagewise = async (rowData) => {
    try {
      setLoading(true);
      toast.info("Preparing download...");

      const center_id = localStorage.getItem("center_id");
      const class_id = rowData.class_id || selectedClass;
      const month = rowData.month || "January";

      const params = new URLSearchParams({
        class_id,
        center_id,
        month,
      });

      const response = await fetch(
        // `${serverUrl}/download-result-report?${params}`,

        `${serverUrl}/download-subject-percentage?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const contentType = response.headers.get("content-type");

      // First check if the response is successful
      if (!response.ok) {
        // Handle all error responses (including 500)
        let errorData;

        try {
          // Try to parse as JSON first
          errorData = await response.json();
        } catch (jsonError) {
          // If not JSON, get the text response (for HTML errors)
          const errorText = await response.text();
          throw new Error(
            `Server error: ${response.status} - ${errorText.substring(
              0,
              200
            )}...`
          );
        }

        // Create user-friendly error messages
        let errorMessage = "";
        let errorTitle = "";

        if (errorData.validation_type === "exam_validation") {
          errorTitle = "📝 Exams Not Scheduled";
          errorMessage = `The following subjects exams are not being held:\n${errorData.missing_subjects.join(
            ", "
          )}`;
        } else if (errorData.validation_type === "award_list_validation") {
          errorTitle = "📊 Award Lists Missing";
          errorMessage = `Award lists are not filled for the following subjects:\n${errorData.missing_subjects.join(
            ", "
          )}`;
        } else if (errorData.data) {
          errorTitle = "👥 Missing Student Results";
          errorMessage = errorData.data
            .map(
              (student) =>
                `${student.roll_no} (${
                  student.name
                }): ${student.missing_subjects.join(", ")}`
            )
            .join("\n");
        } else {
          errorTitle = "❌ Error";
          errorMessage =
            errorData.message || `Server error: ${response.status}`;
        }

        // Show detailed error toast
        toast.error(
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              {errorTitle}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
          </div>,
          {
            autoClose: 2000,
            closeOnClick: false,
            draggable: false,
          }
        );
        return;
      }

      // If we get here, response is successful (200-299)
      if (
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        // Successful download handling
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers.get("content-disposition");
        let filename = `monthly_results_${class_id}_${month}.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("✅ Download started successfully!");
      } else {
        // Handle unexpected successful response format
        console.warn(
          "Unexpected content-type for successful response:",
          contentType
        );
        const responseText = await response.text();
        throw new Error(`Unexpected response format: ${contentType}`);
      }
    } catch (error) {
      console.error("Error downloading results:", error);

      // More specific error messages
      if (error.message.includes("Server error:")) {
        toast.error(`🚨 Server Error: ${error.message}`);
      } else if (error.message.includes("Unexpected response format")) {
        toast.error(
          "❌ Server returned unexpected format. Please check server logs."
        );
      } else {
        toast.error(`❌ ${error.message || "Failed to download results"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 500,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <div
              className="downloadButton"
              onClick={() => handleDownloadResult(params.row)}
            >
              Download Result
            </div>
            <div
              className="downloadButton"
              onClick={() => handleDownloadResultPercentagewise(params.row)}
            >
              Download Result Percentage wise
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="datatable">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-3 pb-2 lg:pb-2">
        <div className="text-gray-700 text-2xl">Monthly Results</div>
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          <div className="buttonGroup">
            <Link
              to="/monthly-results/new"
              className="commonButton w-full sm:w-auto"
            >
              Add New
            </Link>
            {roles.includes("Admin") && (
              <FormControl size="small" sx={{ minWidth: 200, marginLeft: 2 }}>
                <InputLabel id="class-select-label">Select Class</InputLabel>
                <Select
                  labelId="class-select-label"
                  value={selectedClass}
                  label="Select Class"
                  onChange={handleClassChange}
                  disabled={classLoading}
                >
                  {classLoading ? (
                    <MenuItem value="">Loading classes...</MenuItem>
                  ) : classes.length > 0 ? (
                    classes.map((classItem) => (
                      <MenuItem key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No classes available</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </div>
        </div>
      </div>
      {loading ? (
        <Loader fullScreen={false} />
      ) : data.length === 0 ? (
        <div className="noDataMessage">No monthly results available</div>
      ) : (
        <DataGrid
          className="datagrid"
          rows={data}
          columns={monthlyresultColumns.concat(actionColumn)}
          pageSize={9}
          rowsPerPageOptions={[9]}
          components={{
            Toolbar: GridToolbar,
          }}
          getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
        />
      )}
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default MonthlyResultDataTable;
